<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import * as XLSX from 'xlsx';
import CampaignsAPI from 'dashboard/api/campaigns';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const stats = ref(null);
const loading = ref(true);
const error = ref(null);
let pollTimer = null;

const pct = val => {
  if (!stats.value || stats.value.total === 0) return 0;
  return Math.round((val / stats.value.total) * 100);
};

const sentPct = computed(() =>
  pct(
    (stats.value?.sent ?? 0) +
      (stats.value?.delivered ?? 0) +
      (stats.value?.read ?? 0)
  )
);
const deliveredPct = computed(() =>
  pct((stats.value?.delivered ?? 0) + (stats.value?.read ?? 0))
);
const readPct = computed(() => pct(stats.value?.read ?? 0));
const failedPct = computed(() => pct(stats.value?.failed ?? 0));
// eslint-disable-next-line no-unused-vars
const pendingPct = computed(() => pct(stats.value?.pending ?? 0));

const isRunning = computed(
  () =>
    stats.value?.campaign_status === 'active' && (stats.value?.pending ?? 0) > 0
);

const fetchStats = async () => {
  try {
    const { data } = await CampaignsAPI.getStats(props.campaign.id);
    stats.value = data;
    error.value = null;
  } catch (e) {
    error.value = 'Erro ao carregar estatísticas';
  } finally {
    loading.value = false;
  }
};

const statusLabel = status =>
  ({
    sent: 'Enviado',
    delivered: 'Entregue',
    read: 'Lido',
    failed: 'Falhou',
    pending: 'Pendente',
  })[status] ?? status;
const statusColor = status =>
  ({
    sent: 'text-n-slate-11',
    delivered: 'text-n-teal-11',
    read: 'text-n-brand',
    failed: 'text-n-red-9',
    pending: 'text-n-slate-10',
  })[status] ?? '';
const statusIcon = status =>
  ({
    sent: 'i-lucide-check',
    delivered: 'i-lucide-check-check',
    read: 'i-lucide-eye',
    failed: 'i-lucide-x-circle',
    pending: 'i-lucide-clock',
  })[status] ?? '';
const statusBadge = status =>
  ({
    sent: 'bg-n-alpha-3 text-n-slate-11',
    delivered: 'bg-n-teal-3 text-n-teal-11',
    read: 'bg-n-brand/10 text-n-brand',
    failed: 'bg-n-red-3 text-n-red-9',
    pending: 'bg-n-alpha-2 text-n-slate-10',
  })[status] ?? '';

const formatTime = ts =>
  ts
    ? new Date(ts).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '—';

const exportXLS = () => {
  if (!stats.value?.messages?.length) return;

  const wb = XLSX.utils.book_new();

  // Resumo sheet
  const summaryData = [
    ['Campanha', stats.value.title],
    ['Total', stats.value.total],
    ['Enviados', stats.value.sent],
    ['Entregues', stats.value.delivered],
    ['Lidos', stats.value.read],
    ['Falharam', stats.value.failed],
    ['Pendentes', stats.value.pending],
  ];
  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  wsSummary['!cols'] = [{ wch: 14 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumo');

  // Detalhamento sheet
  const headers = [
    'Nome',
    'Telefone',
    'Status',
    'Enviado em',
    'Entregue em',
    'Lido em',
    'Mensagem de Erro',
  ];
  const rows = stats.value.messages.map(m => [
    m.contact_name ?? '',
    m.phone ?? '',
    statusLabel(m.status),
    m.sent_at ? new Date(m.sent_at).toLocaleString('pt-BR') : '',
    m.delivered_at ? new Date(m.delivered_at).toLocaleString('pt-BR') : '',
    m.read_at ? new Date(m.read_at).toLocaleString('pt-BR') : '',
    m.error_message ?? '',
  ]);
  const wsDetail = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  wsDetail['!cols'] = [
    { wch: 28 },
    { wch: 18 },
    { wch: 12 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 40 },
  ];
  XLSX.utils.book_append_sheet(wb, wsDetail, 'Detalhamento');

  const filename = `campanha_${stats.value.campaign_id}_${stats.value.title.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
  XLSX.writeFile(wb, filename);
};

onMounted(() => {
  fetchStats();
  pollTimer = setInterval(fetchStats, 10000);
});

onUnmounted(() => clearInterval(pollTimer));
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
<template>
  <!-- Overlay -->
  <div
    class="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
    @click="emit('close')"
  />

  <!-- Drawer -->
  <div
    class="fixed top-0 ltr:right-0 rtl:left-0 z-50 h-full w-[30rem] max-w-full bg-n-surface-1 border-l border-n-weak shadow-2xl flex flex-col"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <div class="flex flex-col gap-0.5 min-w-0">
        <h3 class="text-base font-medium text-n-slate-12 truncate">
          {{ campaign.title }}
        </h3>
        <span class="text-xs text-n-slate-11">Relatório de Disparos</span>
      </div>
      <div class="flex items-center gap-2">
        <button
          v-if="!loading && stats && stats.total > 0"
          class="inline-flex items-center gap-1.5 rounded border border-n-weak bg-n-alpha-2 px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-3 transition-colors"
          title="Exportar Excel"
          @click="exportXLS"
        >
          <span class="i-lucide-download w-3.5 h-3.5" />
          Exportar Excel
        </button>
        <button
          v-if="!loading"
          class="inline-flex items-center justify-center w-8 h-8 rounded text-n-slate-11 hover:bg-n-alpha-2"
          title="Atualizar"
          @click="fetchStats"
        >
          <span class="i-lucide-refresh-cw w-4 h-4" />
        </button>
        <button
          class="inline-flex items-center justify-center w-8 h-8 rounded text-n-slate-11 hover:bg-n-alpha-2"
          @click="emit('close')"
        >
          <span class="i-lucide-x w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto">
      <!-- Loading -->
      <div
        v-if="loading"
        class="flex items-center justify-center py-20 text-n-slate-11"
      >
        <span class="i-lucide-loader-circle w-6 h-6 animate-spin" />
      </div>

      <!-- Error -->
      <div
        v-else-if="error"
        class="flex items-center justify-center py-20 text-n-red-9 text-sm"
      >
        {{ error }}
      </div>

      <!-- Sem disparos registrados -->
      <div
        v-else-if="stats.total === 0"
        class="flex flex-col items-center justify-center py-20 gap-3 text-n-slate-11 px-6 text-center"
      >
        <span class="i-lucide-inbox w-12 h-12 opacity-30" />
        <span class="text-sm font-medium">Nenhum disparo registrado ainda</span>
        <span class="text-xs text-n-slate-10">O disparo ocorre automaticamente no horário agendado.</span>
      </div>

      <template v-else>
        <!-- Cards de resumo -->
        <div class="grid grid-cols-5 gap-2 px-6 py-5 border-b border-n-weak">
          <div
            class="flex flex-col items-center gap-1 rounded-xl bg-n-alpha-2 py-3 px-1"
          >
            <span class="text-xl font-bold text-n-slate-12">{{
              stats.total
            }}</span>
            <span class="text-[10px] text-n-slate-11">Total</span>
          </div>
          <div
            class="flex flex-col items-center gap-1 rounded-xl bg-n-alpha-2 py-3 px-1"
          >
            <span class="text-xl font-bold text-n-slate-11">{{
              stats.sent
            }}</span>
            <span class="text-[10px] text-n-slate-11">Enviados</span>
          </div>
          <div
            class="flex flex-col items-center gap-1 rounded-xl bg-n-alpha-2 py-3 px-1"
          >
            <span class="text-xl font-bold text-n-teal-11">{{
              stats.delivered
            }}</span>
            <span class="text-[10px] text-n-slate-11">Entregues</span>
          </div>
          <div
            class="flex flex-col items-center gap-1 rounded-xl bg-n-alpha-2 py-3 px-1"
          >
            <span class="text-xl font-bold text-n-brand">{{ stats.read }}</span>
            <span class="text-[10px] text-n-slate-11">Lidos</span>
          </div>
          <div
            class="flex flex-col items-center gap-1 rounded-xl bg-n-alpha-2 py-3 px-1"
          >
            <span class="text-xl font-bold text-n-red-9">{{
              stats.failed
            }}</span>
            <span class="text-[10px] text-n-slate-11">Falharam</span>
          </div>
        </div>

        <!-- Barra de progresso -->
        <div class="px-6 py-5 border-b border-n-weak">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-medium text-n-slate-11">Progresso do disparo</span>
            <span class="text-xs font-semibold text-n-slate-12">{{ deliveredPct }}% entregue · {{ readPct }}% lido</span>
          </div>
          <div
            class="h-3 w-full rounded-full bg-n-alpha-2 overflow-hidden flex"
          >
            <div
              class="h-full bg-n-brand transition-all duration-500"
              :style="{ width: readPct + '%' }"
            />
            <div
              class="h-full bg-n-teal-9 transition-all duration-500"
              :style="{ width: deliveredPct - readPct + '%' }"
            />
            <div
              class="h-full bg-n-slate-9 transition-all duration-500"
              :style="{ width: sentPct - deliveredPct + '%' }"
            />
            <div
              class="h-full bg-n-red-9 transition-all duration-500"
              :style="{ width: failedPct + '%' }"
            />
          </div>
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5">
            <span class="flex items-center gap-1.5 text-[11px] text-n-slate-11">
              <span
                class="w-2.5 h-2.5 rounded-full bg-n-brand flex-shrink-0"
              />Lidos {{ readPct }}%
            </span>
            <span class="flex items-center gap-1.5 text-[11px] text-n-slate-11">
              <span
                class="w-2.5 h-2.5 rounded-full bg-n-teal-9 flex-shrink-0"
              />Entregues {{ deliveredPct }}%
            </span>
            <span class="flex items-center gap-1.5 text-[11px] text-n-slate-11">
              <span
                class="w-2.5 h-2.5 rounded-full bg-n-slate-9 flex-shrink-0"
              />Enviados {{ sentPct }}%
            </span>
            <span class="flex items-center gap-1.5 text-[11px] text-n-slate-11">
              <span
                class="w-2.5 h-2.5 rounded-full bg-n-red-9 flex-shrink-0"
              />Falharam {{ failedPct }}%
            </span>
          </div>
          <div
            v-if="isRunning"
            class="flex items-center gap-2 mt-3 text-xs text-n-brand"
          >
            <span class="i-lucide-loader-circle w-3.5 h-3.5 animate-spin" />
            Disparo em andamento · atualizando a cada 10s
          </div>
        </div>

        <!-- Lista detalhada -->
        <div class="px-6 py-5">
          <p
            class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider mb-3"
          >
            Detalhamento por número
          </p>
          <div class="flex flex-col gap-2">
            <div
              v-for="msg in stats.messages"
              :key="msg.phone"
              class="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 bg-n-alpha-2"
            >
              <div class="flex items-center gap-2 min-w-0">
                <span
                  class="w-4 h-4 flex-shrink-0"
                  :class="[statusIcon(msg.status), statusColor(msg.status)]"
                />
                <div class="flex flex-col min-w-0">
                  <span
                    v-if="msg.contact_name"
                    class="text-xs font-medium text-n-slate-12 truncate"
                    >{{ msg.contact_name }}</span>
                  <span class="text-xs text-n-slate-10 font-mono truncate">{{
                    msg.phone || '—'
                  }}</span>
                </div>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0">
                <span
                  v-if="msg.error_message"
                  class="text-[11px] text-n-red-9 max-w-[110px] truncate"
                  :title="msg.error_message"
                >
                  {{ msg.error_message }}
                </span>
                <span
                  v-else-if="msg.sent_at"
                  class="text-[11px] text-n-slate-10"
                >
                  {{ formatTime(msg.sent_at) }}
                </span>
                <span
                  class="text-[11px] font-medium px-2 py-0.5 rounded-full"
                  :class="[statusBadge(msg.status)]"
                >
                  {{ statusLabel(msg.status) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
